<template>
  <div class="deal-card__menu-button">
    <button ref="menuButton" @click.stop="handleMenuButtonClick">
      <HdIcon :src="menuIcon" height="20" width="20" />
    </button>
    <transition name="fade">
      <Portal v-if="isOpen" to="root">
        <menu ref="menu" :style="menuStyle" class="deal-card__menu">
          <li
            v-for="(item, index) in menuItems"
            :key="`deal-card__menu-item-${index}`"
            class="deal-card__menu-item"
          >
            <HdIcon :src="item.icon" height="24" width="24" />
            <router-link
              v-if="item.routeName"
              :to="{ name: item.routeName, query: item.routeQuery }"
            >
              {{ item.label }}
            </router-link>
            <button v-else @click.stop="item.action">{{ item.label }}</button>
          </li>
        </menu>
        <div class="deal-card__menu-overlay" @click="handleClickOutsideMenu" />
      </Portal>
    </transition>
  </div>
</template>

<script>
import { HdIcon, ScrollingService } from 'homeday-blocks';
import { photoCollection, kebabMenu } from 'homeday-assets';
import SCANNER_ROUTE_NAMES from '@/router/scanner/ROUTE_NAMES';
import { MUTATION } from '@/store/CONSTANTS';

const { freezeScrolling, unfreezeScrolling } = ScrollingService;

export default {
  name: 'DealCardMenu',
  components: { HdIcon },
  props: {
    deal: {
      type: Object,
      required: false,
      default: () => ({}),
    },
  },
  data() {
    return {
      menuIcon: kebabMenu,
      isOpen: false,
      buttonRect: null,
    };
  },
  computed: {
    dealUid() {
      return this.deal?.uid;
    },
    menuItems() {
      return [
        {
          // TODO: use this icon from homeday-assets once it's available
          icon: require('@/assets/icons/addNote.svg'),
          label: this.$t('DASHBOARD.CARD.MENU.ADD_NOTE'),
          action: () => {
            this.handleMenuButtonClick();
            this.$store.commit(
              MUTATION.DASHBOARD.TOGGLE_CREATE_NOTE_MODAL_VISIBILITY,
              this.dealUid,
            );
          },
        },
        {
          icon: photoCollection,
          label: this.$t('DASHBOARD.CARD.MENU.DOCUMENT_SCANNER'),
          routeName: SCANNER_ROUTE_NAMES.DOCUMENTS_SCANNER,
          routeQuery: { uid: this.dealUid },
        },
      ];
    },
    menuStyle() {
      const { buttonRect } = this;
      const menuWidthWithoutSpacing = 258 - 12;
      const scrollX = window.scrollX || window.pageXOffset;
      const scrollY = window.scrollY || window.pageYOffset;

      return buttonRect
        ? {
            top: `${buttonRect.top + buttonRect.height + scrollY}px`,
            left: `${buttonRect.left + scrollX - menuWidthWithoutSpacing}px`,
          }
        : {};
    },
  },
  methods: {
    handleMenuButtonClick() {
      this.isOpen = !this.isOpen;
      if (this.isOpen) {
        freezeScrolling(this.$el);
        this.buttonRect = this.$refs.menuButton.getBoundingClientRect();
      } else {
        unfreezeScrolling(this.$el);
      }
    },
    handleClickOutsideMenu({ target: clickedElement }) {
      if (this.$refs.menu && !this.$refs.menu.contains(clickedElement))
        this.handleMenuButtonClick();
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/styles/mixins.scss';

.deal-card__menu {
  background-color: $primary-bg;
  border-radius: $sp-s;
  box-shadow: 0px 0px 24px rgba(26, 27, 60, 0.16);
  opacity: 10;
  width: 258px;
  padding: $sp-m + $sp-s;
  position: absolute;
  z-index: 31;

  &-button > button {
    display: flex;
    z-index: 10;

    > svg {
      margin-top: $sp-s;
    }
  }

  &-item {
    align-items: center;
    display: flex;
    gap: $sp-s;

    > a {
      color: $primary-color;
      text-decoration: none;
    }

    &:not(:last-of-type) {
      margin-bottom: $sp-s + $sp-xs;
    }
  }

  &-button > button,
  &-item > button {
    font-family: inherit;
    font-size: inherit;
    color: inherit;
    background-color: transparent;
    border: none;
    cursor: pointer;
    padding: 0;
  }

  &-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.3);
    z-index: 30;
  }
}

.fade-enter-active {
  animation: fadeIn 0.3s forwards;
}

.fade-leave-active {
  animation: fadeOut 0.3s forwards;
}
</style>
